import style from "./Dashboard.module.css";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SwapHorizontalCircleIcon from "@mui/icons-material/SwapHorizontalCircle";
import BlockIcon from "@mui/icons-material/Block";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { Cell, Pie, PieChart, ResponsiveContainer, Sector } from "recharts";
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  where,
  limit,
} from "@firebase/firestore";
import { db } from "../../services/firebase";
import { AxiosError } from "axios";
import { apiClient } from "../../services/api";
import { useAuth } from "../../providers/AuthProvider";
import Snackbar from "@mui/material/Snackbar";
import {
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { hasAnyGrant, hasGrant } from "../../services/shared";

interface DashLead {
  uid: string;
  status: string;
  distributionChannel: string;
  uidSender: string;
}

interface RulesChart {
  uid?: string;
  name: string;
  value: number;
  fill: string;
  strokeFill: string;
}

interface ProvinceEntry {
  provincia: string;
  value: number;
}

interface StatsData {
  [channel: string]: ProvinceEntry[];
}

const STATUS_COLORS: Record<string, string> = {
  "quality check": "#f59e0b",
  "attesa privacy": "#3b82f6",
  inviato: "#22c55e",
  neutralizzato: "#ef4444",
};

const STATUS_LABELS: Record<string, string> = {
  "quality check": "Quality Check",
  "attesa privacy": "Attesa Privacy",
  inviato: "Inviato",
  neutralizzato: "Neutralizzato",
};

const RULES_COLORS = ["#8d76f1", "#ffa46c"];

function buildDashboardQuery(db: any, uid: string, grants: string[]) {
  const colRef = collection(db, "leads");
  const startOfYear = new Date(new Date().getFullYear(), 0, 1);
  const conditions: any[] = [
    where("createdAt", ">=", Timestamp.fromDate(startOfYear)),
  ];
  if (!hasAnyGrant(grants, ["admin", "quality_op"])) {
    conditions.push(where("uidSender", "==", uid));
  }
  conditions.push(orderBy("createdAt", "desc"));
  conditions.push(limit(500));
  return query(colRef, ...conditions);
}

function fetchUsersByOrg(org: string): Promise<Set<string>> {
  return getDocs(query(collection(db, "users"), where("org", "==", org))).then(
    (snap) => new Set(snap.docs.map((d) => d.id))
  );
}

const renderActiveShape = (props: any) => {
  const RADIAN = Math.PI / 180;
  const {
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    payload,
    percent,
    value,
  } = props;
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = cx + (outerRadius + 10) * cos;
  const sy = cy + (outerRadius + 10) * sin;
  const mx = cx + (outerRadius + 30) * cos;
  const my = cy + (outerRadius + 30) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 22;
  const ey = my;
  const textAnchor = cos >= 0 ? "start" : "end";

  return (
    <g>
      <text x={cx} y={cy} dy={8} fontSize={13} textAnchor="middle" fill={fill}>
        {payload.name}
      </text>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={payload.fill || fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 6}
        outerRadius={outerRadius + 10}
        fill={payload.strokeFill || fill}
      />
      <path
        d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
        stroke={payload.strokeFill || fill}
        fill="none"
      />
      <circle
        cx={ex}
        cy={ey}
        r={2}
        fill={payload.strokeFill || fill}
        stroke="none"
      />
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        textAnchor={textAnchor}
        fill="#333"
      >
        {`${value}`}
      </text>
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        dy={18}
        textAnchor={textAnchor}
        fill="#999"
      >
        {`(${(percent * 100).toFixed(1)}%)`}
      </text>
    </g>
  );
};

export default function Dashboard() {
  const { user, grants, token } = useAuth();

  const [leads, setLeads] = useState<DashLead[]>([]);
  const [loadingLeads, setLoadingLeads] = useState<boolean>(true);
  const [errorLeads, setErrorLeads] = useState<string | null>(null);
  const [leadsAreCapped, setLeadsAreCapped] = useState<boolean>(false);

  const [rulesData, setRulesData] = useState<RulesChart[]>([]);
  const [loadingRules, setLoadingRules] = useState<boolean>(true);

  const [stats, setStats] = useState<StatsData | null>(null);

  const [orgMateUids, setOrgMateUids] = useState<Set<string>>(new Set());
  const [adminOrgUids, setAdminOrgUids] = useState<Set<string>>(new Set());
  const [selectedOrg, setSelectedOrg] = useState<string>("");
  const [orgList, setOrgList] = useState<{ uid: string; name: string }[]>([]);

  const [snackState, setSnackState] = useState<boolean>(false);
  const [snackMessage, setSnackMessage] = useState<{
    severity: any;
    message: string;
  }>({ severity: "error", message: "" });

  useEffect(() => {
    if (!hasGrant(grants, "quality_op") || hasGrant(grants, "admin") || !user?.org) return;
    fetchUsersByOrg(user.org)
      .then(setOrgMateUids)
      .catch((e) => console.error("Errore caricamento utenti org:", e));
  }, [grants, user?.org]);

  useEffect(() => {
    if (!hasGrant(grants, "admin")) return;
    getDocs(query(collection(db, "organizations"), where("active", "==", true)))
      .then((snap) => {
        const orgs = snap.docs
          .map((d) => ({ uid: d.id, name: d.data().ragioneSociale as string }))
          .filter((o) => Boolean(o.name))
          .sort((a, b) => a.name.localeCompare(b.name));
        setOrgList(orgs);
      })
      .catch((e) => console.error("Errore caricamento org list:", e));
  }, [grants]);

  useEffect(() => {
    if (!selectedOrg || !hasGrant(grants, "admin")) {
      setAdminOrgUids(new Set());
      return;
    }
    fetchUsersByOrg(selectedOrg)
      .then(setAdminOrgUids)
      .catch((e) => console.error("Errore filtro org:", e));
  }, [selectedOrg, grants]);

  useEffect(() => {
    if (!user?.uid || !grants?.length) return;
    setLoadingLeads(true);
    const q = buildDashboardQuery(db, user.uid, grants);
    const unsub = onSnapshot(
      q,
      (snap) => {
        const data: DashLead[] = snap.docs.map((d) => ({
          uid: d.id,
          status: d.data().status ?? "",
          distributionChannel: d.data().distributionChannel ?? "",
          uidSender: d.data().uidSender ?? "",
        }));
        setLeads(data);
        setLeadsAreCapped(data.length === 500);
        setLoadingLeads(false);
      },
      (err) => {
        console.error(err);
        setErrorLeads("Errore nel caricamento dei lead.");
        setLoadingLeads(false);
      }
    );
    return () => unsub();
  }, [user?.uid, grants]);

  useEffect(() => {
    setLoadingRules(true);
    const q = query(collection(db, "rules"), where("active", "==", true));
    const unsub = onSnapshot(q, (snap) => {
      const data: RulesChart[] = snap.docs.map((d) => ({
        uid: d.id,
        name: d.data().name,
        value: d.data().lead_received,
        fill: RULES_COLORS[0],
        strokeFill: RULES_COLORS[1],
      }));
      setRulesData(data);
      setLoadingRules(false);
    });
    return () => unsub();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await apiClient.get("/v1/stats/dashboard");
      if (res?.status === 200) {
        const result = res.data.data;
        const data: StatsData = {};
        Object.keys(result).forEach((k) => {
          data[k] = Object.keys(result[k])
            .sort()
            .map((key) => ({ provincia: key, value: result[k][key] }));
        });
        setStats(data);
      }
    } catch (e: AxiosError | any) {
      console.error("errore stats", e);
      setSnackMessage({
        severity: "error",
        message: e?.response?.data?.error ?? "Errore nel caricamento statistiche",
      });
      setSnackState(true);
    }
  };

  useEffect(() => {
    if (token) fetchStats();
  }, [token]);

  const filteredLeads = useMemo(() => {
    let result = leads;
    if (hasGrant(grants, "quality_op") && !hasGrant(grants, "admin")) {
      result = result.filter((l) => orgMateUids.has(l.uidSender));
    }
    if (selectedOrg && hasGrant(grants, "admin") && adminOrgUids.size > 0) {
      result = result.filter((l) => adminOrgUids.has(l.uidSender));
    }
    return result;
  }, [leads, orgMateUids, adminOrgUids, selectedOrg, grants]);

  const { statusCounts, channelCounts } = useMemo(() => {
    const sc: Record<string, number> = {};
    const cc: Record<string, { statusMap: Record<string, number>; total: number }> = {
      HUB: { statusMap: {}, total: 0 },
      RETE: { statusMap: {}, total: 0 },
    };
    filteredLeads.forEach((l) => {
      sc[l.status] = (sc[l.status] ?? 0) + 1;
      const ch = l.distributionChannel?.toUpperCase();
      if (ch === "HUB" || ch === "RETE") {
        cc[ch].statusMap[l.status] = (cc[ch].statusMap[l.status] ?? 0) + 1;
        cc[ch].total += 1;
      }
    });
    return { statusCounts: sc, channelCounts: cc };
  }, [filteredLeads]);

  const statusPieData = useMemo(
    () =>
      Object.entries(statusCounts)
        .filter(([, v]) => v > 0)
        .map(([status, value]) => ({
          name: STATUS_LABELS[status] ?? status,
          value,
          fill: STATUS_COLORS[status] ?? "#999",
          strokeFill: STATUS_COLORS[status] ?? "#999",
        })),
    [statusCounts]
  );

  const percInviato = useMemo(() => {
    const total = filteredLeads.length;
    if (!total) return "0";
    return (((statusCounts["inviato"] ?? 0) / total) * 100).toFixed(1);
  }, [filteredLeads.length, statusCounts]);

  const provinceHotCold = useMemo(() => {
    if (!stats) return null;
    const result: Record<string, { hot: ProvinceEntry[]; cold: ProvinceEntry[] }> = {};
    Object.keys(stats).forEach((channel) => {
      const sorted = [...stats[channel]]
        .filter((p) => p.value > 0)
        .sort((a, b) => b.value - a.value);
      result[channel] = {
        hot: sorted.slice(0, 5),
        cold: [...sorted].reverse().slice(0, 5),
      };
    });
    return result;
  }, [stats]);

  const kpiCards = [
    {
      label: "Totale Lead",
      value: filteredLeads.length,
      colorClass: style.recordDefault,
      icon: <AccountCircleIcon sx={{ fontSize: 16, color: "#959595" }} />,
    },
    {
      label: "Quality Check",
      value: statusCounts["quality check"] ?? 0,
      colorClass: style.recordAmber,
      icon: <HourglassEmptyIcon sx={{ fontSize: 16, color: "#f59e0b" }} />,
    },
    {
      label: "Attesa Privacy",
      value: statusCounts["attesa privacy"] ?? 0,
      colorClass: style.recordBlue,
      icon: <HourglassEmptyIcon sx={{ fontSize: 16, color: "#3b82f6" }} />,
    },
    {
      label: "Inviato",
      value: statusCounts["inviato"] ?? 0,
      colorClass: style.recordGreen,
      icon: <CheckCircleIcon sx={{ fontSize: 16, color: "#22c55e" }} />,
    },
    {
      label: "Neutralizzato",
      value: statusCounts["neutralizzato"] ?? 0,
      colorClass: style.recordRed,
      icon: <BlockIcon sx={{ fontSize: 16, color: "#ef4444" }} />,
    },
    {
      label: "% Qualificati",
      value: `${percInviato}%`,
      colorClass: style.recordViolet,
      icon: <TrendingUpIcon sx={{ fontSize: 16, color: "#8b5cf6" }} />,
    },
  ];

  return (
    <div className="container">
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={snackState}
        onClose={() => setSnackState(false)}
        autoHideDuration={3000}
      >
        <Alert
          onClose={() => setSnackState(false)}
          severity={snackMessage.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackMessage.message}
        </Alert>
      </Snackbar>

      <h3>Dashboard</h3>

      {errorLeads && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {errorLeads}
        </Alert>
      )}
      {leadsAreCapped && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Visualizzando i primi 500 lead dell'anno. I dati potrebbero essere parziali.
        </Alert>
      )}

      {hasGrant(grants, "admin") && orgList.length > 0 && (
        <div className={style.filterRow}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Filtra per fornitore</InputLabel>
            <Select
              value={selectedOrg}
              label="Filtra per fornitore"
              onChange={(e) => setSelectedOrg(e.target.value)}
            >
              <MenuItem value="">Tutti</MenuItem>
              {orgList.map((org) => (
                <MenuItem key={org.uid} value={org.uid}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      )}

      {loadingLeads ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "2em" }}>
          <CircularProgress size={32} />
        </div>
      ) : (
        <div className={style.kpiRow}>
          {kpiCards.map((card) => (
            <div className={style.dashCard} key={card.label}>
              <div
                className="flex-row"
                style={{ alignItems: "center", gap: ".3em", marginBottom: ".5em" }}
              >
                {card.icon}
                <span className={style.dashCardTitle}>{card.label}</span>
              </div>
              <span className={`${style.dashCardRecord} ${card.colorClass}`}>
                {card.value}
              </span>
            </div>
          ))}
        </div>
      )}

      {!loadingLeads && (
        <>
          <h4 className={style.sectionTitle}>Distribuzione per canale</h4>
          <div className={style.chartContainer}>
            {(["HUB", "RETE"] as const).map((ch) => (
              <div className={style.channelPanel} key={ch}>
                <div
                  className="flex-row"
                  style={{ alignItems: "center", gap: ".5em", marginBottom: ".75em" }}
                >
                  <SwapHorizontalCircleIcon sx={{ fontSize: 18, color: "#959595" }} />
                  <span style={{ fontWeight: 600, fontSize: "14px", color: "#333" }}>
                    {ch}
                  </span>
                  <Chip
                    label={channelCounts[ch].total}
                    size="small"
                    sx={{ fontSize: "11px", height: 20 }}
                  />
                </div>
                {Object.entries(STATUS_LABELS).map(([statusKey, statusLabel]) => (
                  <div
                    key={statusKey}
                    className="flex-row"
                    style={{ justifyContent: "space-between", marginBottom: ".35em" }}
                  >
                    <span style={{ fontSize: "13px", color: "#656565" }}>
                      {statusLabel}
                    </span>
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: STATUS_COLORS[statusKey],
                      }}
                    >
                      {channelCounts[ch].statusMap[statusKey] ?? 0}
                    </span>
                  </div>
                ))}
              </div>
            ))}

            {statusPieData.length > 0 && (
              <div className="flex-col" style={{ minWidth: 280, alignItems: "flex-start" }}>
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#666",
                    marginBottom: ".25em",
                  }}
                >
                  Distribuzione stati
                </span>
                <ResponsiveContainer
                  minWidth="280px"
                  minHeight="280px"
                  width="100%"
                  height="100%"
                  aspect={1}
                >
                  <PieChart>
                    <Pie
                      activeShape={renderActiveShape}
                      data={statusPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      dataKey="value"
                    >
                      {statusPieData.map((entry, index) => (
                        <Cell key={`status-cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </>
      )}

      {loadingRules ? (
        <CircularProgress size={24} sx={{ display: "block", margin: "1em auto" }} />
      ) : rulesData.length > 0 ? (
        <>
          <h4 className={style.sectionTitle}>Distribuzione regole dispatching</h4>
          <div style={{ maxWidth: 360 }}>
            <ResponsiveContainer
              minWidth="280px"
              minHeight="280px"
              width="100%"
              height="100%"
              aspect={1}
            >
              <PieChart>
                <Pie
                  activeShape={renderActiveShape}
                  data={rulesData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </>
      ) : null}

      {provinceHotCold && (
        <>
          <h4 className={style.sectionTitle}>Province per canale</h4>
          <div className={style.chartContainer}>
            {Object.keys(provinceHotCold).map((channel) => (
              <div className={style.provincePanel} key={channel}>
                <div
                  className="flex-row"
                  style={{ alignItems: "center", gap: ".5em", marginBottom: ".75em" }}
                >
                  <SwapHorizontalCircleIcon sx={{ fontSize: 18, color: "#959595" }} />
                  <span style={{ fontWeight: 600, fontSize: "14px", color: "#333" }}>
                    {channel.toUpperCase()}
                  </span>
                </div>

                <div className={style.hotColdSection}>
                  <span className={style.hotColdLabel}>🔥 Top 5 calde</span>
                  {provinceHotCold[channel].hot.map((p) => (
                    <div
                      key={`hot-${p.provincia}`}
                      className="flex-row"
                      style={{ justifyContent: "space-between", marginBottom: ".25em" }}
                    >
                      <span style={{ fontSize: "13px", color: "#656565" }}>
                        {p.provincia.toUpperCase()}
                      </span>
                      <span
                        style={{ fontSize: "13px", fontWeight: 600, color: "#22c55e" }}
                      >
                        {p.value}
                      </span>
                    </div>
                  ))}
                </div>

                <div className={style.hotColdSection} style={{ marginTop: ".75em" }}>
                  <span className={style.hotColdLabel}>❄️ Top 5 fredde</span>
                  {provinceHotCold[channel].cold.map((p) => (
                    <div
                      key={`cold-${p.provincia}`}
                      className="flex-row"
                      style={{ justifyContent: "space-between", marginBottom: ".25em" }}
                    >
                      <span style={{ fontSize: "13px", color: "#656565" }}>
                        {p.provincia.toUpperCase()}
                      </span>
                      <span
                        style={{ fontSize: "13px", fontWeight: 600, color: "#3b82f6" }}
                      >
                        {p.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
